import { useAttachBriefDeliverable } from "@entities/brief/lib/hooks/useAttachDeliverable";
import useBulkActions from "@entities/brief/lib/hooks/useBulkActions";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { ToastService } from "@shared/lib/services";
import { BriefNotificationActionType } from "@shared/lib/types/brief";
import {
  IBriefInfluencerSearchRequestV2,
  IBriefInfluencerV2,
} from "@shared/lib/types/briefInfluencerV2";
import { Avatar, Button, Popover } from "antd";
import {
  AlertCircle,
  ArrowRight,
  Ban,
  ExternalLink,
  Trash2
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import styles from "./CampaignDeliverablesWarningModal.module.scss";

export default function CampaignDeliverablesWarningModal({
  preCheckDeliverables,
  open,
  onCancel,
  onBack,
  onSuccess,
  onConfirm,
  handleUpdateInfluencer,
  payload,
}: {
  preCheckDeliverables: {
    status: "success" | "failed" | "warning";
    briefInfluencerId: string;
    reason: string;
    briefInfluencer: IBriefInfluencerV2;
  }[];
  open: boolean;
  onCancel?: () => void;
  onBack?: () => void;
  onSuccess?: () => void;
  onConfirm?: (validChecks: any[]) => void;
  handleUpdateInfluencer?: (
    data: { id: string; briefInfluencer: IBriefInfluencerV2 }[]
  ) => void;
  payload: {
    checkedList?: string[];
    uncheckList?: string[];
    selectAll?: boolean;
    searchPayload: Partial<IBriefInfluencerSearchRequestV2>;
    deliverableId: string;
    resetCost?: boolean;
  };
}) {
  const { briefId } = useBriefStore();
  const { bulkActions, isBulkActions, isBulkActionsSuccess, bulkActionsError } =
    useBulkActions();
  const {
    attachBriefDeliverable,
    isAttached,
    attachBriefDeliverableError,
  } = useAttachBriefDeliverable(briefId);
  const [affectedEntries, setAffectedEntries] = useState(preCheckDeliverables);

  const validChecks = useMemo(() => {
    return affectedEntries?.filter(
      (val) => val.status === "success" || val.status === "warning"
    );
  }, [affectedEntries]);

  useEffect(() => {
    if (open) {
      setAffectedEntries(preCheckDeliverables);
    }
  }, [open, preCheckDeliverables]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        if (onBack) {
          onBack();
        } else {
          onCancel?.();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [open, onBack, onCancel]);

  useEffect(() => {
    if (isBulkActionsSuccess) {
      ToastService.success("Deliverable attached successfully.");
      onSuccess?.();
    } else if (bulkActionsError) {
      ToastService.error(bulkActionsError?.message);
    }
  }, [isBulkActionsSuccess, bulkActionsError, onSuccess]);

  useEffect(() => {
    if (isAttached) {
      ToastService.success("Deliverable attached successfully.");
      onSuccess?.();
    } else if (attachBriefDeliverableError) {
      ToastService.error(attachBriefDeliverableError?.message);
    }
  }, [isAttached, attachBriefDeliverableError, onSuccess]);

  const handleRemoveInfluencer = (id: string) => {
    setAffectedEntries((prev) =>
      prev.filter((i) => i.briefInfluencerId !== id)
    );
  };

  const handleConfirm = () => {
    if (!validChecks?.length) return;
    if (onConfirm) {
      onConfirm(validChecks);
      return;
    }
    const {
      selectAll,
      searchPayload,
      deliverableId,
    } = payload;
    if (selectAll) {
      bulkActions({
        briefId,
        actionType: BriefNotificationActionType.attachDeliverables,
        filterParams: searchPayload,
        isSelectAll: false,
        actionParams: {
          deliverableV2Id: deliverableId,
          resetCost: payload?.resetCost,
        },
        ids: validChecks?.map((val) => val.briefInfluencerId) || [],
        notIncludeIds: [],
      });
    } else {
      attachBriefDeliverable({
        briefInfluencerIds: validChecks.map((i) => i.briefInfluencerId || ""),
        deliverableId: deliverableId || "",
        resetCost: payload?.resetCost
      });
    }
  };

  const content = (
    <div style={{ width: 440, padding: "4px 0" }}>
      <div className={styles.modalBody}>
        {/* {validChecks?.some((val) => val.status === "warning") ? (
          <div className={styles.warningBox}>
            <div className={styles.warningIcon}>
              <TriangleAlert size={18} />
            </div>
            <div className={styles.warningContent}>
              <h4 className={styles.warningTitle}>Commercials will be reset</h4>
              <p className={styles.warningDescription}>
                Changing deliverables will invalidate current offers. Status
                will change to <strong>&quot;Commercials Pending&quot;</strong>{" "}
                and prices will need to be re-negotiated.
              </p>
            </div>
          </div>
        ) : null} */}

        <div className={styles.creatorsSection}>
          <div className={styles.creatorsHeader}>
            <span className={styles.creatorsTitle}>
              Affected Creators ({affectedEntries.length})
            </span>
          </div>

          <div className={styles.creatorsList}>
            {affectedEntries.map((val) => {
              return (
                <div
                  key={val.briefInfluencerId}
                  className={`${styles.creatorRow} ${
                    val.status === "failed" ? styles.creatorRowPurchased : ""
                  }`}
                >
                  <div className={styles.creatorLeft}>
                    <Avatar
                      src={val.briefInfluencer?.socialMedia?.profilePicUrl}
                      className={styles.creatorAvatar}
                    />
                    <div className={styles.creatorInfo}>
                      <div
                        className={`${styles.creatorName} ${
                          val.status === "failed"
                            ? styles.creatorNamePurchased
                            : ""
                        }`}
                      >
                        {val.briefInfluencer?.socialMedia?.link ? (
                          <a
                            href={val.briefInfluencer?.socialMedia?.link}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className={styles.creatorHandle}
                          >
                            @{val.briefInfluencer?.socialMedia?.username}{" "}
                            <ExternalLink size={10} strokeWidth={2.5} />
                          </a>
                        ) : (
                          <div className={styles.creatorHandle}>
                            @{val.briefInfluencer?.socialMedia?.username}
                          </div>
                        )}
                      </div>
                      <div className={styles.creatorPrice}>
                        Current:{" "}
                        {val.briefInfluencer?.brandCost?.cost
                          ? `₹${val.briefInfluencer?.brandCost.cost.toFixed(2)}`
                          : "Pending"}
                      </div>
                    </div>
                  </div>

                  <div className={styles.creatorRight}>
                    {val.status === "failed" ? (
                      <div className={styles.purchasedTag}>
                        <Ban size={12} />
                        <span>{val.reason}</span>
                      </div>
                    ) : (
                      <Button
                        type="text"
                        className={styles.removeButton}
                        icon={<Trash2 size={14} />}
                        onClick={() =>
                          handleRemoveInfluencer(val.briefInfluencerId)
                        }
                      />
                    )}
                  </div>
                </div>
              );
            })}

            {affectedEntries.length === 0 && (
              <div className={styles.emptyState}>No creators selected.</div>
            )}
          </div>
        </div>
      </div>

      <div
        className={styles.modalFooter}
        style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #e3e8ee" }}
      >
        <div className={styles.footerNote}>
          {affectedEntries?.length - validChecks?.length > 0 && (
            <span className={styles.footerNoteText}>
              <AlertCircle size={12} className={styles.footerNoteIcon} />{" "}
              {affectedEntries?.length - validChecks?.length} creator(s) will
              be skipped.
            </span>
          )}
        </div>
        <div className={styles.footerActions}>
          <Button size="small" onClick={() => onCancel?.()}>
            Cancel
          </Button>
          <Button
            size="small"
            type="primary"
            onClick={handleConfirm}
            disabled={validChecks?.length === 0}
            loading={isBulkActions}
            icon={<ArrowRight size={14} />}
          >
            Confirm Changes
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={(newOpen) => {
        if (!newOpen) {
          onCancel?.();
        }
      }}
      placement="right"
      trigger={["click"]}
      destroyTooltipOnHide

      title={
        <span style={{ fontSize: 13, fontWeight: 600, color: "#1a1f36" }}>
          Confirm Deliverables Change
        </span>
      }
      content={content}
    >
      <span />
    </Popover>
  );
}
